 
import type { CreateServiceData, Service, ServiceQueryParams, ServiceStats, UpdateServiceData } from '../../../shared/types/services';
import api from '../axios';
import type { BrandService } from './brand';

// Services Service Class
export class ServicesService {
  // Service CRUD Operations
  async createService(data: CreateServiceData): Promise<Service> {
    const response = await api.post<Service>('/services', data);
    return response.data;
  }

  async getServices(params?: ServiceQueryParams): Promise<Service[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.isActive !== undefined) {
      queryParams.append('isActive', params.isActive.toString());
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.category) {
      queryParams.append('category', params.category);
    }

    const url = `/services${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get<Service[]>(url);
    return response.data;
  }

  async getActiveServices(): Promise<Service[]> {
    const response = await api.get<Service[]>('/services/active');
    return response.data;
  }

  async getServiceByCode(code: string): Promise<Service> {
    const response = await api.get<Service>(`/services/by-code/${code}`);
    return response.data;
  }

  async getServiceById(id: string): Promise<Service> {
    const response = await api.get<Service>(`/services/${id}`);
    return response.data;
  }

  async getServiceBrands(id: string): Promise<BrandService[]> {
    const response = await api.get<BrandService[]>(`/services/${id}/brands`);
    return response.data;
  }

  async getServiceTenants(id: string): Promise<any[]> {
    const response = await api.get<any[]>(`/services/${id}/tenants`);
    return response.data;
  }

  async getServiceStats(id: string): Promise<ServiceStats> {
    const response = await api.get<ServiceStats>(`/services/${id}/stats`);
    return response.data;
  }

  async updateService(id: string, data: UpdateServiceData): Promise<Service> {
    const response = await api.patch<Service>(`/services/${id}`, data);
    return response.data;
  }

  async deleteService(id: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`/services/${id}`);
    return response.data;
  }

  async toggleServiceStatus(id: string): Promise<Service> {
    const response = await api.post<Service>(`/services/${id}/toggle`);
    return response.data;
  }

  // Utility Methods
  async searchServices(searchTerm: string): Promise<Service[]> {
    return this.getServices({ search: searchTerm });
  }

  async getServicesByStatus(isActive: boolean): Promise<Service[]> {
    return this.getServices({ isActive });
  }

  async getServiceCategories(): Promise<string[]> {
    const response = await api.get<string[]>('/services/categories');
    return response.data;
  }

  // Bulk Operations (if implemented)
  async bulkCreateServices(services: CreateServiceData[]): Promise<Service[]> {
    const response = await api.post<Service[]>('/services/bulk-create', services);
    return response.data;
  }

  async bulkToggleServices(serviceIds: string[], isActive: boolean): Promise<Service[]> {
    const response = await api.post<Service[]>('/services/bulk-toggle', {
      serviceIds,
      isActive
    });
    return response.data;
  }
}

// Create and export singleton instance
export const servicesService = new ServicesService();

// Export individual methods for convenience
export const {
  createService,
  getServices,
  getActiveServices,
  getServiceByCode,
  getServiceById,
  getServiceBrands,
  getServiceTenants,
  getServiceStats,
  updateService,
  deleteService,
  toggleServiceStatus,
  searchServices,
  getServicesByStatus,
  getServiceCategories,
  bulkCreateServices,
  bulkToggleServices
} = servicesService;